import { HttpInterceptorFn, HttpErrorResponse, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, switchMap, throwError } from 'rxjs';
import { AuthService } from '../services/auth.service';

const CSRF_SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

/** Re-clone `req` with current Bearer + CSRF headers from AuthService.
 *  Used both on the initial send and when retrying after a CSRF refresh. */
function withAuthHeaders(req: HttpRequest<unknown>, auth: AuthService): HttpRequest<unknown> {
  const headers: Record<string, string> = {};
  const token = auth.getSessionToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (!CSRF_SAFE_METHODS.has(req.method.toUpperCase())) {
    const csrf = auth.getCsrfToken();
    if (csrf) headers['X-CSRF-Token'] = csrf;
  }
  return req.clone({ withCredentials: true, setHeaders: headers });
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const auth = inject(AuthService);

  // Session lives in an HttpOnly cookie set by /login; withCredentials lets
  // the browser ship it. iOS Safari ITP blocks the cross-origin
  // `SameSite=None` cookie between rcma-app and rcma-api, so we ALSO send
  // the token (stored in localStorage so it survives PWA close and browser
  // restart) as `Authorization: Bearer …`.
  // The server accepts either. CSRF token on every state-changing call.
  req = withAuthHeaders(req, auth);

  return next(req).pipe(
    catchError((err: HttpErrorResponse) => {
      // 401 on a protected request means the session expired (or was revoked).
      // Clear local state and bounce to /login with a returnUrl so the user
      // lands back where they were after re-authenticating. We deliberately
      // skip /auth/* endpoints — a 401 there is "wrong credentials", not
      // session expiry, and is handled by the login form itself.
      if (err.status === 401 && !/\/auth\//.test(req.url)) {
        auth.clearSession();
        const current = router.url;
        const onLogin = current.split('?')[0] === '/login';
        if (!onLogin) {
          router.navigate(['/login'], { queryParams: { returnUrl: current } });
        }
        return throwError(() => err);
      }

      // 403 + "CSRF token …" means we have a valid session but no CSRF
      // header — happens after a PWA/tab close when sessionStorage was
      // wiped but localStorage (with the session token) survived. The
      // constructor already kicks off a /me refresh on app start; this
      // catches the race where the user mutates faster than /me returns.
      // Retry the request EXACTLY ONCE after pulling a fresh CSRF from
      // /me. The `_retry` marker on the cloned request prevents loops
      // if the refresh itself somehow fails to produce a CSRF.
      const csrfFailure = err.status === 403
        && /csrf/i.test(err.error?.error || '')
        && !/\/auth\//.test(req.url)
        && !(req.headers.get('X-Retry') === '1')
        && !!auth.getSessionToken();
      if (csrfFailure) {
        return auth.checkSession().pipe(
          switchMap(() => {
            const retried = withAuthHeaders(req.clone({ setHeaders: { 'X-Retry': '1' } }), auth);
            return next(retried);
          }),
          catchError((refreshErr) => throwError(() => refreshErr))
        );
      }

      return throwError(() => err);
    })
  );
};
